import first from "lodash-es/first";

import { Button } from "@bokio/designsystem/components/Button/Button";
import Icon from "@bokio/designsystem/components/Icon/Icon";
import { LoadingSpinner } from "@bokio/designsystem/components/Loading";
import { useHiddenFileInput } from "@bokio/hooks/useHiddenFileInput/useHiddenFileInput";
import { AccountingLangFactory } from "@bokio/lang";
import ExpensesLangFactory from "@bokio/lang/ExpensesLangFactory";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import { toEnvelopeEndpoint, useLazyApi } from "@bokio/mobile-web-shared/hooks/useApi/useApi";
import * as proxy from "@bokio/mobile-web-shared/services/api/proxy";
import { mergeClassNames } from "@bokio/utils/classes";
import { prepareImageForUpload } from "@bokio/utils/uploadUtils";

import type { ButtonProps } from "@bokio/designsystem/components/Button/Button";
import type { FC, ReactNode } from "react";

import * as styles from "./addReceiptExtraPageButton.scss";

const VALID_FILE_MIME_TYPES = ["application/pdf", "image/png", "image/jpeg"];

const uploadExtraPageEndpoint = async (params: {
	file: File;
	companyId: string;
	receiptId: string;
	saveAsTemporary?: boolean;
}) => {
	const { file, companyId, receiptId, saveAsTemporary } = params;

	if (file.type === "application/pdf") {
		return proxy.Accounting.ReceiptController.UploadPdfExtraPage.Post(
			companyId,
			file,
			receiptId,
			saveAsTemporary ? "true" : "false",
		);
	} else if (file.type === "image/png" || file.type === "image/jpeg") {
		const image = await prepareImageForUpload(file);

		return proxy.Accounting.ReceiptController.UploadReceiptExtraPageWithFile.Post(
			companyId,
			new File([image.Data], image.Name, { type: image.Type }),
			receiptId,
			saveAsTemporary === true,
		);
	}
	throw Error("Invalid file type");
};

type AddReceiptExtraPageButtonHookProps = {
	receiptId: string;
	onUploadStarted?: () => void;
	pageAdded?: () => void;
	saveAsTemporary?: boolean;
	allowMultiple?: boolean;
};

type AddReceiptExtraPageButtonProps = {
	label?: string;
	buttonProps?: Omit<ButtonProps, "onClick">;
} & AddReceiptExtraPageButtonHookProps;

type AddReceiptExtraPageButtonForReceiptOverviewProps = {
	disabled?: boolean;
	solidBackground?: boolean;
} & AddReceiptExtraPageButtonHookProps;

const useAddReceiptExtraPageButton = ({
	receiptId,
	onUploadStarted,
	pageAdded,
	saveAsTemporary,
}: AddReceiptExtraPageButtonHookProps) => {
	const companyId = useCompanyInfo().companyInfo.Id;

	const [uploadExtraPage, uploadExtraPageRequest] = useLazyApi(toEnvelopeEndpoint(uploadExtraPageEndpoint));

	const { renderHiddenFileInput, triggerFileDialog } = useHiddenFileInput({
		acceptFileType: VALID_FILE_MIME_TYPES.join(","),
		testIdOfHiddenFileInput: "UploadReceiptExtraPage_Input",
		onSelectedFilesChange: async (files: File[]) => {
			const file = first(files);

			if (!file) {
				return;
			}

			if (!VALID_FILE_MIME_TYPES.includes(file.type)) {
				return;
			}

			onUploadStarted && onUploadStarted();
			const response = await uploadExtraPage({
				file,
				companyId: companyId,
				receiptId: receiptId,
				saveAsTemporary,
			});

			if (response.Success) {
				pageAdded?.();
			}
		},
	});

	return { renderHiddenFileInput, triggerFileDialog, isLoading: uploadExtraPageRequest.isLoading };
};

type AddReceiptExtraPageButtonBaseProps = {
	disabled?: boolean;
	solidBackground?: boolean;
	isLoading?: boolean;
	onClick: () => void;
	children?: ReactNode;
	testId?: string;
};

export const AddReceiptExtraPageButtonBase: FC<AddReceiptExtraPageButtonBaseProps> = ({
	disabled,
	solidBackground,
	isLoading,
	testId,
	onClick,
}) => {
	const accountingLang = AccountingLangFactory();

	return (
		<button
			className={mergeClassNames(styles.addPageButton, !!solidBackground && styles.addPageButtonSolidBackground)}
			data-testid={testId || "AddReceiptExtraPageButton"}
			type="button"
			disabled={isLoading || disabled}
			onClick={e => {
				if (!isLoading) {
					onClick();
				}

				e.stopPropagation();
			}}
		>
			{isLoading ? (
				<LoadingSpinner />
			) : (
				<span>
					<>
						<Icon name="plus" size="22" />
						<span className={styles.addPageText}>{accountingLang.AddPage}</span>
					</>
				</span>
			)}
		</button>
	);
};

export const AddReceiptExtraPageButtonForReceiptOverview = ({
	disabled,
	solidBackground,
	...hookProps
}: AddReceiptExtraPageButtonForReceiptOverviewProps) => {
	const { renderHiddenFileInput, triggerFileDialog, isLoading } = useAddReceiptExtraPageButton(hookProps);

	return (
		<div>
			{renderHiddenFileInput()}
			<AddReceiptExtraPageButtonBase
				disabled={disabled}
				solidBackground={solidBackground}
				isLoading={isLoading}
				onClick={triggerFileDialog}
				testId="AddReceiptExtraPageButton_Overview"
			/>
		</div>
	);
};

export const AddReceiptExtraPageButton = ({ label, buttonProps, ...hookProps }: AddReceiptExtraPageButtonProps) => {
	const expensesLang = ExpensesLangFactory();
	const { renderHiddenFileInput, triggerFileDialog } = useAddReceiptExtraPageButton(hookProps);

	return (
		<div>
			{renderHiddenFileInput()}
			<Button {...buttonProps} testId="AddReceiptExtraPage_Button" onClick={triggerFileDialog}>
				{label ? label : expensesLang.AddExtraPage}
			</Button>
		</div>
	);
};
